import { useCallback, useMemo } from 'react';
import { Link, Outlet, useLocation, useMatch, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type { GoalActivityProject, ProjectDetail } from '@localagent/sdk';
import { KanbanBoard } from '../../components/work/KanbanBoard';
import { ProjectCard } from '../../components/work/ProjectCard';
import {
  BLOCKERS_UNAVAILABLE,
  PROJECT_LANES,
  PROJECT_LANE_LABELS,
  assigneeResolver,
  blockerIndex,
  goalProjects,
  groupProjectsByLane,
  nodeLabelIndex,
  type BlockerList,
} from '../../lib/project-board-helpers';
import { projectDetailToBoard } from '../../lib/work-management-helpers';
import { usePublishViewSnapshot } from '../../components/shogun/usePublishViewSnapshot';
import { boardRecords, projectRecords, scopeRecord } from '../../lib/view-snapshot-helpers';
import { workItemSessionsAtom } from '../../stores/work/atoms';
import { useTeams } from '../../hooks/useWorkManagement';
import { errorWithNothingCachedToShow } from '../../lib/query-error';
import { useGoalActivity, useWorkOrderings } from '../../hooks/useWorkManagement';
import { usePositions } from '../../hooks/useOrgChart';
import { getSDK } from '../../services/sdk-instance';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

function ProjectLaneColumn({
  lane,
  projects,
  initiativeNames,
  hrefFor,
  resolvePosition,
  blockersFor,
}: {
  lane: (typeof PROJECT_LANES)[number];
  projects: GoalActivityProject[];
  initiativeNames: Map<string, string>;
  hrefFor: (projectId: string) => string;
  resolvePosition: (id?: string | null) => string;
  blockersFor: (nodeId: string) => BlockerList;
}) {
  return (
    <div className="w-64 flex-shrink-0" data-testid={`project-lane-${lane}`} data-count={projects.length}>
      <div className="h-full rounded-lg bg-card">
        <div className="p-3">
          <div className="flex items-center justify-between">
            <h3 className="text-label font-semibold">{PROJECT_LANE_LABELS[lane]}</h3>
            <span className="text-caption text-muted-foreground">{projects.length}</span>
          </div>
        </div>
        <div className="min-h-[200px] space-y-2 p-2">
          {projects.map((project) => {
            const initiativeName = initiativeNames.get(project.id);
            if (initiativeName === undefined) {
              throw new Error(`project ${project.id} is not under any initiative`);
            }
            return (
              <ProjectCard
                key={project.id}
                project={project}
                to={hrefFor(project.id)}
                assigneeTitle={resolvePosition(project.assignee_position_id)}
                initiativeName={initiativeName}
                blockers={blockersFor(project.id)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ProjectBoard() {
  const { teamId, goalId } = useParams<{ teamId: string; goalId: string }>();
  const { data: activity, isLoading, error } = useGoalActivity(goalId ?? null);
  const activityError = errorWithNothingCachedToShow(activity, error);
  const { data: orderings, error: orderingsQueryError } = useWorkOrderings(goalId ?? null);
  const orderingsError = errorWithNothingCachedToShow(orderings, orderingsQueryError);
  const { data: positions, error: positionsQueryError } = usePositions();
  const positionsError = errorWithNothingCachedToShow(positions, positionsQueryError);

  const entries = useMemo(() => goalProjects(activity?.initiatives), [activity]);
  const lanes = useMemo(
    () => groupProjectsByLane(entries.map((entry) => entry.project)),
    [entries]
  );
  const initiativeNames = useMemo(
    () => new Map(entries.map((entry) => [entry.project.id, entry.initiativeName])),
    [entries]
  );
  const resolve = useMemo(() => assigneeResolver(positions), [positions]);
  const blockers = useMemo(
    () => blockerIndex(orderings, nodeLabelIndex(activity?.initiatives)),
    [orderings, activity]
  );
  const blockersFor = useCallback(
    (nodeId: string): BlockerList =>
      orderingsError ? BLOCKERS_UNAVAILABLE : blockers.get(nodeId),
    [orderingsError, blockers]
  );

  const { data: teams } = useTeams();
  const { pathname } = useLocation();
  const snapshot = useMemo(() => {
    if (!activity || !teamId || !goalId) return null;
    const { counts, visible } = projectRecords(
      entries.map((entry) => entry.project),
      (nodeId) => blockers.get(nodeId),
    );
    return {
      route: pathname,
      context: {
        surface: 'work',
        primary_type: 'project',
        scope: [
          scopeRecord('team', teamId, teams?.find((t) => t.id === teamId)?.name ?? 'Team'),
          scopeRecord('goal', goalId, activity.goal.objective),
        ],
        counts,
        visible,
      },
    };
  }, [activity, teamId, goalId, teams, entries, blockers, pathname]);
  usePublishViewSnapshot(snapshot);

  if (!teamId || !goalId) {
    throw new Error('ProjectBoard requires :teamId and :goalId route params');
  }
  const goalPath = `/work/teams/${teamId}/goals/${goalId}`;

  return (
    <div className="flex h-full flex-col" data-testid="project-board">
      <div className="flex items-center justify-between gap-4 border-b px-6 py-3">
        <div className="min-w-0">
          {activity && (
            <>
              <h2 className="truncate text-label font-semibold" title={activity.goal.objective}>
                {activity.goal.objective}
              </h2>
              <p className="text-caption text-muted-foreground" data-testid="project-board-count">
                {entries.length} projects
              </p>
            </>
          )}
        </div>
        <Link to={`${goalPath}/roadmap`} className="text-caption text-primary hover:underline" data-testid="project-board-roadmap-link">
          Roadmap
        </Link>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {isLoading && <LoadingState label="Loading projects..." />}
        {activityError && (
          <p className="text-body text-destructive" data-testid="project-board-error">
            Failed to load projects: {activityError.message}
          </p>
        )}
        {orderingsError && (
          <p className="text-body text-destructive" data-testid="project-board-blockers-error">
            Blockers unavailable: {orderingsError.message}
          </p>
        )}
        {positionsError && (
          <p className="text-body text-destructive" data-testid="project-board-assignees-error">
            Assignees unavailable: {positionsError.message}
          </p>
        )}
        {activity && (
          <div className="flex gap-3 overflow-x-auto pb-4">
            {PROJECT_LANES.map((lane) => (
              <ProjectLaneColumn
                key={lane}
                lane={lane}
                projects={lanes[lane]}
                initiativeNames={initiativeNames}
                hrefFor={(projectId) => `${goalPath}/projects/${projectId}`}
                resolvePosition={resolve.position}
                blockersFor={blockersFor}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function useProjectDetail(projectId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<ProjectDetail, Error>({
    // Under the ['work','tickets'] prefix so a ticket mutation's invalidation reaches it.
    queryKey: ['work', 'tickets', 'project', projectId],
    queryFn: () => getSDK().workManagement.getProject(projectId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && projectId),
  });
}

export function ProjectTicketBoard() {
  const { teamId, goalId, projectId } = useParams<{
    teamId: string;
    goalId: string;
    projectId: string;
  }>();
  const { data: detail, isLoading, error } = useProjectDetail(projectId);
  const detailError = errorWithNothingCachedToShow(detail, error);
  const board = useMemo(() => (detail ? projectDetailToBoard(detail) : null), [detail]);

  const { data: teams } = useTeams();
  const { data: activity } = useGoalActivity(goalId ?? null);
  const sessions = useAtomValue(workItemSessionsAtom);
  const { pathname } = useLocation();
  // The ticket route is a CHILD match, so `useParams` here cannot see it.
  const ticketId = useMatch(
    '/work/teams/:teamId/goals/:goalId/projects/:projectId/tickets/:ticketId',
  )?.params.ticketId;

  const snapshot = useMemo(() => {
    if (!board || !detail || !teamId || !goalId || !projectId) return null;
    const { counts, visible } = boardRecords(board, sessions);
    return {
      route: pathname,
      context: {
        surface: 'work',
        primary_type: 'work_item',
        scope: [
          scopeRecord('team', teamId, teams?.find((t) => t.id === teamId)?.name ?? 'Team'),
          scopeRecord('goal', goalId, activity?.goal.objective ?? 'Goal'),
          scopeRecord('project', projectId, detail.project.name),
        ],
        counts,
        visible,
        ...(ticketId
          ? {
              selected: {
                type: 'work_item',
                id: ticketId,
                label: visible.find((r) => r.id === ticketId)?.label ?? 'Ticket',
              },
            }
          : {}),
      },
    };
  }, [board, detail, teamId, goalId, projectId, teams, activity, sessions, pathname, ticketId]);
  usePublishViewSnapshot(snapshot);

  if (!teamId || !goalId || !projectId) {
    throw new Error('ProjectTicketBoard requires :teamId, :goalId and :projectId route params');
  }

  return (
    <div className="flex h-full min-w-0 flex-1" data-testid="project-ticket-board">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between gap-4 border-b px-6 py-3">
          <div className="min-w-0">
            {detail && (
              <h2 className="truncate text-label font-semibold" title={detail.project.name}>
                {detail.project.name}
              </h2>
            )}
            {detail && (
              <p
                className="text-caption text-muted-foreground"
                data-testid="project-ticket-board-progress"
                data-total={detail.progress.total_tickets}
                data-completed={detail.progress.breakdown.completed}
              >
                {detail.progress.breakdown.completed}/{detail.progress.total_tickets} tickets ·{' '}
                {detail.progress.completion_percentage}% complete
              </p>
            )}
          </div>
          <Link
            to={`/work/teams/${teamId}/goals/${goalId}/projects`}
            className="text-caption text-primary hover:underline"
            data-testid="project-ticket-board-back"
          >
            All projects
          </Link>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {isLoading && <LoadingState label="Loading tickets..." />}
          {detailError && (
            <p className="text-body text-destructive" data-testid="project-ticket-board-error">
              Failed to load tickets: {detailError.message}
            </p>
          )}
          {board && <KanbanBoard board={board} />}
        </div>
      </div>

      <Outlet />
    </div>
  );
}
